import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingState from '../../components/LoadingState';
import StatusBadge from '../../components/StatusBadge';
import Pagination from '../../components/ui/Pagination';
import SearchInput from '../../components/ui/SearchInput';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { EmptyState, ErrorState } from '../../components/ui/States';
import {
  DEBIT_NOTE_STATUS_LABELS,
  DebitNote,
  GOODS_STATE_LABELS,
  GoodsState,
  creditedAmountOf,
  listDebitNotes,
} from '../../lib/debitNoteApi';
import { formatDate } from '../../utils/dateFormat';
import { GOODS_STATE_TONES } from './debitNoteDisplay';

/**
 * The Credit Notes module's Vendor tab (D44, 2026-09-17): United's vendor return
 * receipts (DN-00001), never mixed into the client list, register or totals.
 *
 * Amounts are shown in each return's own currency and never summed across rows:
 * a vendor list mixes TT$ and US$ invoices.
 */

const LOAD_FAILED = 'Vendor returns could not be loaded. Check your connection and try again.';

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'needs_admin', label: 'Needs an Admin' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'awaiting_approval', label: 'Awaiting approval' },
  { value: 'approved', label: 'Approved' },
];

function money(value: number | null | undefined, currency: string) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'TTD', maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

/** What the vendor's own credit note says so far, in words. */
function vendorCreditNoteLabel(note: DebitNote): string {
  const attached = Boolean(note.vendorCreditNote?.number);
  if (!attached) {
    return ['sent', 'awaiting_approval', 'approved'].includes(note.status) ? 'Pending' : '--';
  }
  switch (note.amountCheck?.state) {
    case 'mismatch':
      return `${note.vendorCreditNote?.number} · amount differs`;
    case 'use_vendor_requested':
      return `${note.vendorCreditNote?.number} · vendor's amount with Admin`;
    default:
      return String(note.vendorCreditNote?.number);
  }
}

/**
 * Where a goods return's goods are, beside its status, only while that asks for
 * attention or is final (VR-3): held, waiting on a write-off, written off. The
 * rest (released, returned, restored) need nothing more from anyone, and the
 * return itself says them.
 */
const LISTED_GOODS_STATES: GoodsState[] = ['held', 'write_off_requested', 'written_off'];

const GoodsStateBadge: React.FC<{ note: DebitNote }> = ({ note }) => {
  const state = note.kind === 'goods' ? note.goods?.state ?? null : null;
  if (!state || !LISTED_GOODS_STATES.includes(state)) return null;
  return <StatusBadge status={GOODS_STATE_LABELS[state]} tone={GOODS_STATE_TONES[state]} />;
};

export const VendorReturnsList: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<DebitNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const limit = 25;

  const load = async () => {
    setLoading(true);
    try {
      const res = await listDebitNotes({ page, limit, status, q: search });
      setItems(res.data || []);
      setTotal(res.meta?.total || 0);
      setError(null);
    } catch {
      setItems([]);
      setTotal(0);
      setError(LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (page !== 1) setPage(1);
      else void load();
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
        <SegmentedControl
          aria-label="Vendor return status"
          value={status}
          onChange={(value: string) => {
            setStatus(value);
            setPage(1);
          }}
          segments={STATUS_FILTERS}
        />
        <SearchInput value={search} onChange={setSearch} placeholder="DN, vendor, invoice" className="w-48" />
      </div>

      {error ? (
        <div className="p-4">
          <ErrorState message={error} onRetry={load} />
        </div>
      ) : (
        <div className="overflow-auto">
          <table className="data-table min-w-[1000px]">
            <thead>
              <tr>
                <th>DN number</th>
                <th>Vendor</th>
                <th>Vendor invoice</th>
                <th>Type</th>
                <th>Date</th>
                <th className="text-right">Total</th>
                <th>Vendor credit note</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="py-8">
                    <LoadingState text="Loading vendor returns..." />
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10">
                    <EmptyState
                      title="No vendor returns found"
                      detail="Use New vendor return to send goods back to a supplier or claim money off one of their invoices."
                    />
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((note) => (
                  <tr key={note._id} className="cursor-pointer" onClick={() => navigate(`/credit-notes/vendor/${note._id}`)}>
                    <td className="font-medium text-slate-900">{note.debitNoteNumber}</td>
                    <td>{note.vendorName || '--'}</td>
                    <td>{note.vendorInvoiceNumber || '--'}</td>
                    <td>{note.kind === 'goods' ? 'Goods' : 'Money only'}</td>
                    <td>{formatDate(note.createdAt, '--')}</td>
                    <td className="text-right font-semibold tabular-nums">
                      {money(creditedAmountOf(note), note.currency)}
                    </td>
                    <td>{vendorCreditNoteLabel(note)}</td>
                    <td>
                      <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge status={DEBIT_NOTE_STATUS_LABELS[note.status] || note.status} />
                        <GoodsStateBadge note={note} />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} pageSize={limit} total={total} onPageChange={setPage} noun="vendor returns" />
    </div>
  );
};

export default VendorReturnsList;
